// MF-399 — selector de round (milestone) en la pantalla de marking.
// Tabs por round + botón "Finalize round" + indicador de carry-forward. Al
// cambiar de tab, el reducer re-proyecta los answers (question.rounds -> answer)
// sin ir al servidor. Assessment legacy (rounds vacío) => no renderiza nada.
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import {
  finalizeAssessmentRound,
  reopenAssessmentRound,
  assessmentMarkingBulk
} from '../../../../../services/data-entry/assessments.service'
import {
  loadAssessmentMaking,
  setAssessmentMarkingSelectedRound,
  resetHasChangedAssessmentMarking
} from '../../../../../store/actions/dataEntryAssessmentMarkingActions'

const ROUND_STATUS_FINALIZED = 'finalized'

const fmtDate = (d) => (d ? dayjs(d).format('DD/MMM/YYYY') : '—')

const RoundTabs = ({ canFinalize = false }) => {
  const dispatch = useDispatch()
  const { assessmentId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [finalizing, setFinalizing] = useState(false)
  const [reopenOpen, setReopenOpen] = useState(false)
  const [reopening, setReopening] = useState(false)

  const rounds = useSelector((state) => state?.dataEntryAssessmentMarking?.rounds ?? [])
  const carryForward = useSelector((state) => state?.dataEntryAssessmentMarking?.carryForward ?? true)
  const selectedRoundId = useSelector((state) => state?.dataEntryAssessmentMarking?.selectedRoundId ?? null)
  const saveList = useSelector((state) => state?.dataEntryAssessmentMarking?.saveList ?? [])
  const skip = useSelector((state) => state?.datatable?.assessmentMarking?.skip ?? 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.assessmentMarking?.rowsPerPage ?? 100)
  const filterState = useSelector((state) => state?.filter?.assessmentMarking)

  if (!rounds.length) return null

  const selectedRound = rounds.find((r) => String(r._id) === String(selectedRoundId))
  const isFinalized = selectedRound?.status === ROUND_STATUS_FINALIZED

  const onChangeTab = (_evt, roundId) => {
    dispatch(setAssessmentMarkingSelectedRound(roundId))
  }

  const reloadMarking = () => {
    const filterData = {
      yearLevelId: filterState?.filter?.yearLevelId || [],
      classesCode: filterState?.filter?.classesCode || [],
      studentCode: filterState?.filter?.studentCode || '',
      studentFirstName: filterState?.filter?.studentFirstName || '',
      studentLastName: filterState?.filter?.studentLastName || '',
      studentHouse: filterState?.filter?.studentHouse || '',
      studentForm: filterState?.filter?.studentForm || ''
    }
    dispatch(loadAssessmentMaking(assessmentId, skip * rowsPerPage, rowsPerPage, filterData))
  }

  const onConfirmFinalize = async () => {
    try {
      setFinalizing(true)
      // MF-399 — Flush de guardados PENDIENTES antes de finalizar. El
      // copy-on-finalize (BE) siembra el round siguiente leyendo el answer del
      // round finalizado DESDE la DB; si el autosave (debounce ~1s) aún no
      // aterrizó, la cascada leería un answer viejo y sembraría vacío. Por eso
      // persistimos saveList sincrónicamente y recién ahí finalizamos.
      if (saveList.length) {
        const { data: saveData, error: saveError } = await assessmentMarkingBulk({
          assessmentId,
          records: saveList
        })
        if (saveError || !saveData) {
          enqueueSnackbar('We could not save pending changes before finalizing, please try again', {
            variant: 'error'
          })
          return
        }
        dispatch(resetHasChangedAssessmentMarking())
      }
      const { data, error } = await finalizeAssessmentRound(assessmentId, selectedRoundId)
      if (error || !data) {
        enqueueSnackbar('We could not finalize this round, please try again', { variant: 'error' })
      } else {
        enqueueSnackbar(`Round "${selectedRound?.label}" finalized`, { variant: 'success' })
        setConfirmOpen(false)
        reloadMarking()
      }
    } catch (_error) {
      enqueueSnackbar('We could not finalize this round, please try again', { variant: 'error' })
    } finally {
      setFinalizing(false)
    }
  }

  const onConfirmReopen = async () => {
    try {
      setReopening(true)
      const { data, error } = await reopenAssessmentRound(assessmentId, selectedRoundId)
      if (error || !data) {
        enqueueSnackbar('We could not reopen this round, please try again', { variant: 'error' })
      } else {
        enqueueSnackbar(`Round "${selectedRound?.label}" reopened`, { variant: 'success' })
        setReopenOpen(false)
        reloadMarking()
      }
    } catch (_error) {
      enqueueSnackbar('We could not reopen this round, please try again', { variant: 'error' })
    } finally {
      setReopening(false)
    }
  }

  return (
    <Box className='flex items-center gap-4 flex-wrap'>
      <Tabs
        value={selectedRoundId || false}
        onChange={onChangeTab}
        variant='scrollable'
        scrollButtons='auto'
        className='min-h-0'
      >
        {rounds.map((round) => {
          const finalized = round?.status === ROUND_STATUS_FINALIZED
          const hasDates = round.sittingDate || round.dueDate
          const tabLabel = (
            <span className='flex items-center gap-1.5'>
              {finalized && <FontAwesomeIcon icon='check-circle' style={{ color: '#2e7d32' }} />}
              {round.label}
            </span>
          )
          return (
            <Tab
              key={round._id}
              value={String(round._id)}
              className='min-h-0 py-1 px-3'
              label={
                hasDates ? (
                  <Tooltip
                    title={
                      <span>
                        Sitting: {fmtDate(round.sittingDate)}
                        <br />
                        Due: {fmtDate(round.dueDate)}
                      </span>
                    }
                  >
                    {tabLabel}
                  </Tooltip>
                ) : (
                  tabLabel
                )
              }
            />
          )
        })}
      </Tabs>

      <Divider orientation='vertical' flexItem className='my-1' />

      <Box className='flex items-center gap-2 flex-wrap'>
        {carryForward && (
          <Tooltip title="New rounds start pre-filled with the previous round's marks">
            <Chip size='small' variant='outlined' color='info' label='Carry-forward on' />
          </Tooltip>
        )}

        {isFinalized ? (
          <>
            <Chip size='small' color='success' label='Finalized' />
            {canFinalize && (
              <ButtonStartIcon
                size='small'
                variant='outlined'
                color='warning'
                startIcon={['fal', 'undo']}
                onClick={() => setReopenOpen(true)}
                disabled={!selectedRoundId}
              >
                Reopen round
              </ButtonStartIcon>
            )}
          </>
        ) : (
          canFinalize && (
            <ButtonStartIcon
              size='small'
              variant='outlined'
              color='primary'
              startIcon={['fal', 'flag-checkered']}
              onClick={() => setConfirmOpen(true)}
              disabled={!selectedRoundId}
            >
              Finalize round
            </ButtonStartIcon>
          )
        )}
      </Box>

      {selectedRound && (selectedRound.sittingDate || selectedRound.dueDate) && (
        <>
          <Divider orientation='vertical' flexItem className='my-1' />
          <Tooltip title={`Dates set for ${selectedRound?.label}`}>
            <Box className='flex items-center gap-3' style={{ fontSize: 12, color: '#6b7a8d' }}>
              <span>
                <span style={{ fontWeight: 600 }}>Sitting:</span> {fmtDate(selectedRound.sittingDate)}
              </span>
              <span>
                <span style={{ fontWeight: 600 }}>Due:</span> {fmtDate(selectedRound.dueDate)}
              </span>
            </Box>
          </Tooltip>
        </>
      )}

      <DialogConfirmCancel
        id={`finalize-round-modal`}
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onConfirmFinalize}
        loading={finalizing}
        textConfirm='Finalize'
        title={'Finalize round'}
        onCloseHeader={true}
        maxWidth='xs'
      >
        <span>
          Finalizing <b>{selectedRound?.label}</b> marks it as complete and feeds it to reports and analytics.
          You can still view it, but it should no longer be actively marked. Continue?
        </span>
      </DialogConfirmCancel>

      <DialogConfirmCancel
        id={`reopen-round-modal`}
        open={reopenOpen}
        onCancel={() => setReopenOpen(false)}
        onConfirm={onConfirmReopen}
        loading={reopening}
        textConfirm='Reopen'
        title={'Reopen round'}
        onCloseHeader={true}
        maxWidth='xs'
      >
        <span>
          Reopening <b>{selectedRound?.label}</b> makes it active and editable again so you can keep marking.
          Reports and analytics will re-sync on the next run. Continue?
        </span>
      </DialogConfirmCancel>
    </Box>
  )
}

export default RoundTabs
